/**
 * Theme Provider
 * React context provider for comprehensive theme management
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { 
  ThemeContextType, 
  ThemePreferences, 
  ColorScheme, 
  ThemeMode, 
  CompleteTheme, 
  ThemeColors 
} from './types';
import { getTheme } from './themes';
import {
  applyTheme,
  loadThemePreferences,
  saveThemePreferences,
  resolveThemeMode,
  watchSystemColorScheme,
  getThemeClassName
} from './utils';

// Theme Context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Theme Provider Props
interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
}

/**
 * Theme Provider Component
 * Manages global theme state and provides theme utilities
 */
export function ThemeProvider({ 
  children, 
  defaultMode = 'system' 
}: ThemeProviderProps) {
  // Initialize preferences from localStorage or defaults
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = loadThemePreferences();
    return saved.mode || defaultMode;
  });

  const [systemMode, setSystemMode] = useState<'light' | 'dark'>(() => resolveThemeMode('system'));

  // Derived state - always use 'default' color scheme
  const currentTheme: CompleteTheme = getTheme('default');
  // FORCE DARK MODE - always use dark theme
  const resolvedMode: 'dark' | 'light' = 'dark'; // resolveThemeMode(mode);
  const currentColors: ThemeColors = currentTheme.dark; // resolvedMode === 'dark' ? currentTheme.dark : currentTheme.light;

  // Apply theme to document when mode changes
  useEffect(() => {
    // FORCE DARK MODE
    applyTheme('dark');
    saveThemePreferences('dark');
  }, [mode]);

  // Watch for system color scheme changes
  useEffect(() => {
    const cleanup = watchSystemColorScheme((isDark) => {
      setSystemMode(isDark ? 'dark' : 'light');
      
      // Re-apply theme if mode is 'system'
      if (mode === 'system') {
        applyTheme(mode);
      }
    });
    
    return cleanup;
  }, [mode]);

  // Theme actions
  const handleSetMode = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  const toggleMode = () => {
    const currentResolvedMode = resolveThemeMode(mode);
    const newMode: ThemeMode = currentResolvedMode === 'light' ? 'dark' : 'light';
    setMode(newMode);
  };

  const getThemeClass = () => {
    return getThemeClassName(resolvedMode, 'default');
  };

  const applyThemeDirectly = (newMode?: ThemeMode) => {
    const targetMode = newMode || mode;
    applyTheme(targetMode);
    
    // Update preferences if different
    if (newMode && newMode !== mode) {
      setMode(newMode);
    }
  };

  // Build preferences object for backward compatibility
  const preferences: ThemePreferences = {
    colorScheme: 'default',
    mode,
    systemMode
  };

  // Context value
  const contextValue: ThemeContextType = {
    preferences,
    currentTheme,
    currentColors,
    resolvedMode,
    setColorScheme: () => {}, // No-op for backward compatibility
    setMode: handleSetMode,
    toggleMode,
    getThemeClass,
    applyTheme: applyThemeDirectly
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 * Must be used within a ThemeProvider
 */
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}

/**
 * Hook to get theme-aware colors for specific use cases
 */
export function useThemeColors() {
  const { currentTheme, resolvedMode } = useTheme();
  
  return {
    asset: currentTheme.assetColors[resolvedMode],
    financial: currentTheme.financialColors[resolvedMode],
    chart: currentTheme.chartConfig[resolvedMode].colors,
    ui: resolvedMode === 'dark' ? currentTheme.dark : currentTheme.light
  };
}

/**
 * Hook to get chart configuration for current theme
 */
export function useChartTheme() {
  const { currentTheme, resolvedMode } = useTheme();
  
  return currentTheme.chartConfig[resolvedMode];
}

/**
 * Higher-order component to inject theme props
 */
export function withTheme<P extends object>(
  Component: React.ComponentType<P & { theme: ThemeContextType }>
) {
  return function ThemedComponent(props: P) {
    const theme = useTheme();
    return <Component {...props} theme={theme} />;
  };
}

/**
 * Hook for conditional theme-based styling
 */
export function useThemeClasses(classes: {
  light?: string;
  dark?: string;
  [colorScheme: string]: string | undefined;
}) {
  const { resolvedMode, preferences } = useTheme();
  
  // Priority: specific color scheme > mode-specific > default
  const themeClass = classes[preferences.colorScheme] || 
                     classes[resolvedMode] || 
                     classes.light || 
                     '';
  
  return themeClass;
}

